import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom-v5-compat';
import { useIntl } from 'react-intl';

import Global from '../../store/global-selectors';
import {
  SELLER_RECOMMENDATION_ACTIONED,
  SELLER_RECOMMENDATION_DISMISSED,
  SELLER_RECOMMENDATION_DISPLAYED,
  SELLER_RECOMMENDATION_LEARN_MORE_CLICKED,
} from '../../constants/analytics-events';
import { LocalStorageService } from '../../services/local-storage-service';
import { Segment } from '../../services/analytics-services/segment';
import { markRecommendationActioned, markRecommendationDismissed } from '../../store/seller-recommendations/api/seller-recommendations.api';
import { useGetLink } from '../../hooks/use-get-link';
import { ctaTypeNavigation } from './recommendation-cta-config';
import { RECOMMENDATION_DISPLAY_TYPES } from './recommendation-display-config';
import { RECOMMENDATION_REGISTRY } from './seller-recommendation-registry';
import { useShouldShowSellerRecommendation } from './use-should-show-seller-recommendation';
import { RecommendationPopoverCard } from './components/recommendation-popover-card/recommendation-popover-card';

type SellerRecommendationResult = {
  content: JSX.Element | null;
  isOpen: boolean;
};

export const useSellerRecommendation = (recommendationType: string): SellerRecommendationResult => {
  const entry = RECOMMENDATION_REGISTRY[recommendationType];
  const shouldShow = useShouldShowSellerRecommendation(recommendationType);
  const navigate = useNavigate();
  const intl = useIntl();
  const recommendation = useSelector((state) => Global.selectSellerRecommendationsRecommendationByType(state, { recommendationType }));
  const sellerCurrency = useSelector(Global.selectSellerDefaultCurrency) ?? 'USD';
  const sellerId = useSelector(Global.selectSellerSellerId);

  const [isRecommendationVisible, setIsRecommendationVisible] = useState(true);

  // useGetLink is a hook — must be called before any early return.
  const learnMoreLinkId = entry?.displayConfig.learnMoreLinkId ?? '';
  const learnMoreUrl = useGetLink(learnMoreLinkId);

  const resolvedContent = useMemo(() => entry?.resolveContent?.(recommendation) ?? entry?.displayConfig.content, [entry, recommendation]);

  const descriptionValues = useMemo(
    () => entry?.resolveRecommendationMetaDataValues?.(recommendation, { intl, sellerCurrency }),
    [entry, recommendation, intl, sellerCurrency],
  );

  const hasTrackedDisplayRef = useRef(false);
  useEffect(() => {
    if (hasTrackedDisplayRef.current || !shouldShow || !entry?.analytics) return;
    hasTrackedDisplayRef.current = true;
    Segment.trackEvent(SELLER_RECOMMENDATION_DISPLAYED, entry.analytics.properties(sellerId));
  }, [shouldShow, entry, sellerId]);

  const hideAndTrack = useCallback(
    (eventName: string) => {
      setIsRecommendationVisible(false);

      if (entry?.dismissalStorageKey) {
        LocalStorageService.setItem(entry.dismissalStorageKey, true);
      }

      if (entry?.analytics) {
        Segment.trackEvent(eventName, entry.analytics.properties(sellerId));
      }
    },
    [entry, sellerId],
  );

  const handleDismiss = useCallback(() => {
    if (!entry) return;
    hideAndTrack(SELLER_RECOMMENDATION_DISMISSED);
    markRecommendationDismissed(recommendationType, entry.maxDismissalCount).subscribe();
  }, [hideAndTrack, recommendationType, entry]);

  const handleLearnMoreClick = useCallback(() => {
    if (entry?.analytics) {
      Segment.trackEvent(SELLER_RECOMMENDATION_LEARN_MORE_CLICKED, entry.analytics.properties(sellerId));
    }
  }, [entry, sellerId]);

  const handleAction = useCallback(() => {
    if (!entry) return;
    hideAndTrack(SELLER_RECOMMENDATION_ACTIONED);
    markRecommendationActioned(recommendationType).subscribe();

    if (entry?.cta?.ctaType === ctaTypeNavigation) {
      navigate(entry.cta.navigationUrl);
    }
  }, [hideAndTrack, recommendationType, entry, navigate]);

  const isOpen = !!(entry && shouldShow && isRecommendationVisible);

  if (!entry) {
    return { content: null, isOpen: false };
  }

  const { displayConfig } = entry;

  let content: JSX.Element | null = null;

  if (displayConfig.displayType === RECOMMENDATION_DISPLAY_TYPES.POPOVER_CARD) {
    content = (
      <RecommendationPopoverCard
        description={resolvedContent}
        descriptionValues={descriptionValues}
        illustrationSrc={displayConfig.illustrationSrc}
        learnMoreUrl={learnMoreUrl ? learnMoreUrl : undefined}
        onClose={handleDismiss}
        onLearnMoreClick={handleLearnMoreClick}
        onPrimaryAction={handleAction}
        primaryButtonLabel={displayConfig.buttonLabel}
        title={displayConfig.title}
      />
    );
  }

  return { content, isOpen };
};

export const SellerRecommendationTrigger = ({ recommendationType }: { recommendationType: string }) => {
  const { content } = useSellerRecommendation(recommendationType);
  return content;
};
